/**
 * Release board logic (alignment map V1-F7-06a Release, V1-F7-06b ReleaseBlocked; F7.6): the
 * server's automatic checklist (GET /pack-lots/:id/release-checklist) shown as the board's lines,
 * and which open problems block release in V1 (only those that touch the four guarantees: mixed
 * run, tag clash or reuse, lab fail, one-farmer break, more packed than delivered).
 */
import type { Tone } from './payoutBoard'

export interface ChecklistItem {
  key: string
  ok: boolean
  detail: string[]
  evidence: Array<{ type: string; id: string; code: string | null }>
}

export interface Checklist {
  packLotId: string
  items: ChecklistItem[]
  canRelease: boolean
  labReportIds: string[]
  computedAt: string | null
}

/** Exception types that block a release in V1 (F7.6 field 6, val 10). */
export const BLOCKING_PROBLEMS = new Set(['RUN_MIXED', 'TAG_CONFLICT', 'TAG_REUSED', 'LAB_FAIL'])

/** Problem words for the open-problems line. */
const PROBLEM_WORDS: Record<string, string> = {
  RUN_MIXED: 'mixed run',
  TAG_CONFLICT: 'tag clash',
  TAG_REUSED: 'tag used again',
  LAB_FAIL: 'lab fail',
  BALANCE_OUT_OF_BAND: 'run balance out of band',
  LABEL_MISMATCH: 'label count or pouch check',
  PHOTO_MISSING: 'photo missing',
  CUSTODY_RULE: 'custody rule',
  COUNT_MISMATCH: 'count mismatch',
  LATE_ENTRY: 'late entry',
  REWEIGH_VARIANCE: 'reweigh variance',
  OTHER: 'other problem',
}
export const problemWords = (type: string) =>
  PROBLEM_WORDS[type] ?? type.replace(/_/g, ' ').toLowerCase()

/**
 * Does this open problem block release in V1? The four-guarantee types always do; a CRITICAL
 * "one farmer rule broken" (OTHER) does; a balance problem blocks only when more came out than
 * went in (surplus) — a loss is a console note (F7.4 val 3, F7.6 val 7).
 */
export function problemBlocks(type: string, detail: Record<string, unknown> = {}): boolean {
  if (BLOCKING_PROBLEMS.has(type)) return true
  if (type === 'OTHER') return detail.reason === 'ONE_FARMER_RULE_BROKEN'
  if (type === 'BALANCE_OUT_OF_BAND') {
    const residual = Number(detail.residual_kg ?? detail.residual_pct ?? NaN)
    return (
      detail.direction === 'SURPLUS' ||
      detail.kind === 'SURPLUS' ||
      (Number.isFinite(residual) && residual < 0)
    )
  }
  return false
}

/** "TAG_CONFLICT open on UNIT" (the checklist's exceptions_closed detail) → its parts. */
export function parseProblemLine(line: string): { type: string; subject: string } {
  const m = /^([A-Z_]+) open on ([A-Z_]+)$/.exec(line.trim())
  return m ? { type: m[1] ?? 'OTHER', subject: m[2] ?? '' } : { type: 'OTHER', subject: '' }
}

export interface BoardLine {
  key: string
  title: string
  tone: Tone
  /** The server's findings (problems when not ok). */
  detail: string[]
  /** Open problems: what blocks and what is only listed. */
  blocks?: string[]
  listed?: string[]
}

const LINE_TITLE: Record<string, string> = {
  lab_pass: 'Lab result',
  label_ok: 'Label',
  count_ok: 'Counts',
  upstream_balanced: 'Weight balance',
  storage_declared: 'Storage',
  upstream_clear: 'Holds upstream',
  exceptions_closed: 'Open problems',
}

/** A problem string the server writes for a missing lab result (waiting, not failed). */
const isWaiting = (d: string) => /PENDING|INCOMPLETE/i.test(d)

/**
 * The checklist in the board's order and words. The label line folds in the pouch scans (the
 * board shows "First pouch QR scanned and matched" under Label). Tones: ok = green tick,
 * wait = the lab result is not in yet, bad = red.
 */
export function releaseLines(c: Checklist): BoardLine[] {
  const by = new Map(c.items.map((i) => [i.key, i]))
  const out: BoardLine[] = []
  for (const key of [
    'lab_pass',
    'label_ok',
    'count_ok',
    'upstream_balanced',
    'storage_declared',
    'upstream_clear',
    'exceptions_closed',
  ]) {
    const item = by.get(key)
    if (!item) continue
    if (key === 'label_ok') {
      const scans = by.get('pouch_scans_ok')
      const detail = [...item.detail, ...(scans?.detail ?? [])]
      const ok = item.ok && (scans?.ok ?? true)
      out.push({ key, title: LINE_TITLE[key] ?? key, tone: ok ? 'ok' : 'bad', detail })
      continue
    }
    if (key === 'exceptions_closed') {
      const parsed = item.detail.map(parseProblemLine)
      const blocks = parsed.filter((p) => problemBlocks(p.type)).map((p) => problemWords(p.type))
      const listed = parsed.filter((p) => !problemBlocks(p.type)).map((p) => problemWords(p.type))
      out.push({
        key,
        title: LINE_TITLE[key] ?? key,
        tone: item.ok ? 'ok' : 'bad',
        detail: item.detail,
        blocks,
        listed,
      })
      continue
    }
    const tone: Tone = item.ok
      ? 'ok'
      : key === 'lab_pass' && item.detail.length > 0 && item.detail.every(isWaiting)
        ? 'wait'
        : 'bad'
    out.push({ key, title: LINE_TITLE[key] ?? key, tone, detail: item.detail })
  }
  return out
}

/** How many lines are not green ("2 lines are not green"). */
export const linesNotGreen = (lines: BoardLine[]) => lines.filter((l) => l.tone !== 'ok').length

export interface SignState {
  attested: boolean
  password: string
  reason: string
}

/**
 * Why a decision cannot be signed yet (null = it can). Release needs every line green, the tick
 * and the password; Hold needs a reason and the password. Nobody who ran or cleaned the line releases it
 * (the server allows them a hold).
 */
export function signBlocked(
  decision: 'RELEASE' | 'HOLD',
  s: SignState,
  ctx: { canRelease: boolean; ranTheLine: boolean; status: string; hold: string },
): string | null {
  if (decision === 'RELEASE') {
    if (ctx.ranTheLine) return 'You ran or cleaned this line: another quality person releases it.'
    if (!releasable(ctx.status, ctx.hold))
      return `The lot is ${stateWords(ctx.status, ctx.hold)}: it cannot be released.`
    if (!ctx.canRelease) return 'Disabled while any line is red or waiting. Only Hold is possible.'
  } else {
    if (ctx.status === 'DESTROYED') return 'The lot is destroyed.'
    if (s.reason.trim().length < HOLD_REASON_MIN)
      return `Write the reason for the hold (at least ${HOLD_REASON_MIN} letters).`
  }
  if (!s.attested) return 'Tick that you have read the lab report and checked the label.'
  if (!s.password) return 'Enter your password.'
  return null
}

/** Server: a hold needs a reason of at least 10 characters (lab.constants HOLD_REASON_MIN). */
export const HOLD_REASON_MIN = 10

/** Server releasableState: CLOSED, or RELEASED and held again; never a recalled lot. */
export function releasable(status: string, hold: string): boolean {
  if (hold === 'RECALLED') return false
  return status === 'CLOSED' || (status === 'RELEASED' && hold === 'HELD')
}

const stateWords = (status: string, hold: string) =>
  hold === 'NONE' ? status.toLowerCase() : `${status.toLowerCase()} and ${hold.toLowerCase()}`
